import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Skeleton,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  IoAddCircleOutline,
  IoCheckmarkCircle,
  IoSearchOutline,
  IoSparklesOutline,
} from "react-icons/io5";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/firebase-config";

// Shared picker for tagging someone/something with interests. Fetches the
// full catalog, supports fuzzy search, and lets the user propose a brand-new
// interest — the backend enriches it (aliases, genre, icon) and returns
// it for immediate selection.
//
// Props:
//   selectedIds   Array of interest doc ids currently selected.
//   onChange      (nextIds: string[], allInterests: Interest[]) => void.
//                 Parents convert ids to whatever storage shape they need
//                 (e.g. DocumentReference[] in CreateEventModal).
//   max           Optional selection cap. Over-cap clicks show a toast.
//   columns       SimpleGrid columns, defaults to 2.

// Normalize text for fuzzy contains matching: lowercase, strip diacritics,
// strip anything that isn't alphanumeric.
const normalize = (s) =>
  (s || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

// Simple "contains" match against name + aliases.
const matchesQuery = (interest, q) => {
  if (!q) return true;
  const haystacks = [interest.name, ...(interest.aliases || [])];
  return haystacks.some((h) => normalize(h).includes(q));
};

// An interest is considered "already present" if its normalized name (or
// any alias) equals the query exactly — used to decide whether to show the
// "add as new" row.
const exactlyExists = (allInterests, q) => {
  if (!q) return true;
  return allInterests.some((i) => {
    const haystacks = [i.name, ...(i.aliases || [])];
    return haystacks.some((h) => normalize(h) === q);
  });
};

export const InterestPicker = ({
  selectedIds = [],
  onChange,
  max,
  columns = 2,
}) => {
  const toast = useToast();

  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);

  // ── Initial fetch ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "interests"));
        if (cancelled) return;
        const all = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            // `merged` rows still live in Firestore (so analytics/links
            // survive) but shouldn't appear in the picker.
            .filter((i) => i.status !== "merged" && i.status !== "hidden");
        // Stable alphabetical order for predictable UI.
        all.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setInterests(all);
      } catch (e) {
        console.error("Failed to load interests", e);
        toast({
          title: "Couldn't load interests",
          description: e.message,
          status: "error",
          duration: 3000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  // ── Derived state ──────────────────────────────────────────────────────
  const normalizedQuery = normalize(query);
  const filtered = useMemo(
      () => interests.filter((i) => matchesQuery(i, normalizedQuery)),
      [interests, normalizedQuery],
  );

  const showAddNew =
    normalizedQuery.length >= 2 &&
    !exactlyExists(interests, normalizedQuery) &&
    !creating;

  // ── Interactions ───────────────────────────────────────────────────────
  const commitChange = (nextIds, nextAll) => {
    onChange?.(nextIds, nextAll ?? interests);
  };

  const toggleId = (id) => {
    const isSelected = selectedIds.includes(id);
    if (!isSelected && max && selectedIds.length >= max) {
      toast({
        title: `You can pick up to ${max}`,
        status: "info",
        duration: 2000,
      });
      return;
    }
    const next = isSelected
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    commitChange(next);
  };

  const handleAddNew = async () => {
    const name = query.trim();
    if (!name) return;

    setCreating(true);
    try {
      const fn = httpsCallable(functions, "suggestInterest");
      const res = await fn({ name });
      const interest = res?.data?.interest;
      if (!interest?.id) {
        throw new Error("No interest returned");
      }

      // Merge into local catalog (replace if the server deduped to an
      // existing row, otherwise append). Re-sort to keep the list tidy.
      setInterests((prev) => {
        const without = prev.filter((i) => i.id !== interest.id);
        const next = [...without, interest];
        next.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return next;
      });

      // Auto-select the new/matched interest (respect the cap).
      if (!selectedIds.includes(interest.id)) {
        if (max && selectedIds.length >= max) {
          toast({
            title: `You can pick up to ${max}`,
            description: "Created the interest but didn't select it.",
            status: "info",
            duration: 2500,
          });
        } else {
          commitChange([...selectedIds, interest.id], [
            ...interests.filter((i) => i.id !== interest.id),
            interest,
          ]);
        }
      }

      setQuery("");

      toast({
        title: res.data.created ? `Added "${interest.name}"` : `Found "${interest.name}"`,
        status: "success",
        duration: 2000,
      });
    } catch (e) {
      console.error("Failed to suggest interest", e);
      toast({
        title: "Couldn't add interest",
        description: e.message || "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setCreating(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <VStack align="stretch" spacing={2}>
        <Skeleton height="36px" borderRadius="lg" />
        <SimpleGrid columns={columns} spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height="44px" borderRadius="lg" />
          ))}
        </SimpleGrid>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={3}>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none" h="full">
          <Icon as={IoSearchOutline} color="gray.400" boxSize={4} />
        </InputLeftElement>
        <Input
          placeholder="Search or add your own…"
          borderRadius="lg"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && showAddNew) {
              e.preventDefault();
              handleAddNew();
            }
          }}
          focusBorderColor="#508cf7"
        />
      </InputGroup>

      {showAddNew && (
        <Box
          as="button"
          type="button"
          onClick={handleAddNew}
          px={3}
          py={2}
          borderRadius="lg"
          border="1.5px dashed"
          borderColor="#508cf7"
          bg="blue.50"
          _hover={{ bg: "blue.100" }}
          transition="all 0.15s"
          textAlign="left"
        >
          <HStack spacing={2}>
            <Icon as={IoSparklesOutline} color="#508cf7" boxSize={4} />
            <Text fontSize="sm" color="#226cf1" fontWeight="600" flex="1">
              Add &quot;{query.trim()}&quot; as a new interest
            </Text>
            <Icon as={IoAddCircleOutline} color="#508cf7" boxSize={5} />
          </HStack>
          <Text fontSize="11px" color="gray.500" mt={1} pl={6}>
            We&apos;ll pick an icon and categorize it automatically.
          </Text>
        </Box>
      )}

      {creating && (
        <HStack
          spacing={2}
          px={3}
          py={2}
          borderRadius="lg"
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
        >
          <Spinner size="xs" color="#508cf7" />
          <Text fontSize="xs" color="gray.600">
            Adding &quot;{query.trim() || "new interest"}&quot;…
          </Text>
        </HStack>
      )}

      {filtered.length === 0 && !showAddNew ? (
        <Box
          px={3}
          py={4}
          borderRadius="lg"
          bg="gray.50"
          border="1px dashed"
          borderColor="gray.200"
          textAlign="center"
        >
          <Text fontSize="xs" color="gray.500">
            No matches. Try a different search.
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={columns} spacing={2}>
          {filtered.map((interest) => {
            const selected = selectedIds.includes(interest.id);
            return (
              <Box
                key={interest.id}
                as="button"
                type="button"
                onClick={() => toggleId(interest.id)}
                px={3}
                py={2}
                borderRadius="lg"
                border="1.5px solid"
                borderColor={selected ? "#508cf7" : "gray.200"}
                bg={selected ? "blue.50" : "white"}
                _hover={{ borderColor: "#508cf7", bg: "blue.50" }}
                transition="all 0.15s"
                textAlign="left"
              >
                <HStack spacing={2}>
                  {interest.icon ? (
                    <Image
                      src={interest.icon}
                      alt={interest.name}
                      boxSize="22px"
                      objectFit="contain"
                      borderRadius="full"
                      flexShrink={0}
                    />
                  ) : (
                    <Box
                      w="22px"
                      h="22px"
                      borderRadius="full"
                      bg="gray.100"
                      flexShrink={0}
                    />
                  )}
                  <Text
                    fontSize="sm"
                    color="gray.700"
                    fontWeight="500"
                    flex="1"
                    noOfLines={1}
                  >
                    {interest.name || interest.id}
                  </Text>
                  {selected && (
                    <Icon
                      as={IoCheckmarkCircle}
                      color="#508cf7"
                      boxSize={4}
                      flexShrink={0}
                    />
                  )}
                </HStack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}
    </VStack>
  );
};

export default InterestPicker;
